/** Persist and apply dark/light shell theme (token-driven via data-theme on <html>). */

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "sc_theme_v1";

/** Gallery-only preference. Never written by the product shell. */
export const GALLERY_THEME_STORAGE_KEY = "sc_ds_gallery_theme_v1";

export const THEME_CHANGED_EVENT = "sc-theme-changed";

function isTheme(value: string | null | undefined): value is Theme {
  return value === "dark" || value === "light";
}

/** Read stored preference; invalid or missing → dark (product default). */
export function loadTheme(): Theme {
  if (typeof localStorage === "undefined") return "dark";
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : "dark";
  } catch {
    return "dark";
  }
}

/** Apply theme to <html> without writing storage. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

/** Persist preference, apply, and notify listeners. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // private mode / quota — still apply for this session
  }
  applyTheme(theme);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(THEME_CHANGED_EVENT, { detail: { theme } }),
    );
  }
}

/** Flip dark ↔ light; returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = loadTheme() === "light" ? "dark" : "light";
  setTheme(next);
  return next;
}

/**
 * Design-gallery theme. Light when unset.
 * Does not read or write `THEME_STORAGE_KEY`, so the product default stays dark.
 */
export function loadGalleryTheme(): Theme {
  if (typeof localStorage === "undefined") return "light";
  try {
    const raw = localStorage.getItem(GALLERY_THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : "light";
  } catch {
    return "light";
  }
}

/** Persist the gallery preference and paint it. Product storage is left alone. */
export function setGalleryTheme(theme: Theme): void {
  try {
    localStorage.setItem(GALLERY_THEME_STORAGE_KEY, theme);
  } catch {
    // private mode / quota — still apply for this visit
  }
  applyTheme(theme);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(THEME_CHANGED_EVENT, { detail: { theme, scope: "gallery" } }),
    );
  }
}

/** Flip the gallery theme only. */
export function toggleGalleryTheme(): Theme {
  const next: Theme = loadGalleryTheme() === "light" ? "dark" : "light";
  setGalleryTheme(next);
  return next;
}

/** Gallery theme for the design-system route. Defaults to light. */
export function useGalleryTheme(): Theme {
  const [theme, setThemeState] = useState<Theme>(() => loadGalleryTheme());

  useEffect(() => {
    const sync = () => setThemeState(loadGalleryTheme());
    const onStorage = (event: StorageEvent) => {
      if (event.key === GALLERY_THEME_STORAGE_KEY || event.key === null) sync();
    };
    window.addEventListener(THEME_CHANGED_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return theme;
}

/**
 * Pins a subtree to one theme regardless of the stored preference.
 *
 * The v2 shell is a light design that reuses v1 components (the CAD viewer and
 * its scene, renderer and toolbar all read the theme). Overriding by context
 * keeps those components untouched and, unlike setTheme, does not overwrite the
 * user's own preference for the rest of the app.
 */
const ThemeOverrideContext = createContext<Theme | null>(null);

export function ThemeOverrideProvider({
  theme,
  children,
}: {
  theme: Theme;
  children: ReactNode;
}) {
  return createElement(ThemeOverrideContext.Provider, { value: theme }, children);
}

/** Subscribe to theme for React components (shell + 3D viewer). */
export function useAppTheme(): Theme {
  const override = useContext(ThemeOverrideContext);
  const [theme, setThemeState] = useState<Theme>(() => loadTheme());

  useEffect(() => {
    const sync = () => setThemeState(loadTheme());
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY || e.key === null) sync();
    };
    window.addEventListener(THEME_CHANGED_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return override ?? theme;
}
